// File: typescript/apps/front-end/src/lib/arrayPolyfills.ts
//
// Minimal runtime shim for Array.prototype.findLast / findLastIndex (ES2023). esbuild's
// `target: es2017` down-levels syntax only and injects no runtime shims, and `@tiptap/core`'s focus
// plugin calls `findLast` inside `dispatchTransaction`: on Android System WebView 75/85 that threw
// "n.findLast is not a function" and crashed the DiscussionPage island (FI-160, prod-log review
// 2026-09-16). Installed beside installPromisePolyfills, before any island mounts. Installs only what
// is missing, non-enumerable like the native; a no-op on modern engines. It sits on the eager startup
// graph (AGENTS.md rule 27): keep it this small.

type Predicate = (value: unknown, index: number, array: unknown) => unknown;

function lastIndexMatching(list: ArrayLike<unknown>, predicate: Predicate, thisArg: unknown): number {
    if (typeof predicate !== "function") {
        throw new TypeError("predicate must be a function");
    }
    for (let i = (list.length >>> 0) - 1; i >= 0; i--) {
        if (predicate.call(thisArg, list[i], i, list)) {
            return i;
        }
    }
    return -1;
}

const SHIMS: Record<string, (this: ArrayLike<unknown>, predicate: Predicate, thisArg?: unknown) => unknown> = {
    findLast(predicate, thisArg) {
        const i = lastIndexMatching(this, predicate, thisArg);
        return i < 0 ? undefined : this[i];
    },
    findLastIndex(predicate, thisArg) {
        return lastIndexMatching(this, predicate, thisArg);
    },
};

export function installArrayPolyfills(): void {
    for (const name of Object.keys(SHIMS)) {
        if (typeof (Array.prototype as unknown as Record<string, unknown>)[name] !== "function") {
            Object.defineProperty(Array.prototype, name, { value: SHIMS[name], writable: true, configurable: true });
        }
    }
}
